/**
 * Adapter: bank-analyser parsed statement rows → fusion canvas elements.
 *
 * This is the "consume-direction" mapping — a really-parsed statement becomes the
 * bank surface on the fusion canvas: one account node + its top counterparties,
 * with Dr/Cr edges. Deterministic (real ledger lines), so court-safe view keeps
 * them. Optionally links the account into the case identity hub. Element data
 * shape matches fusionSeed's nodeEl/edgeEl so the same stylesheet renders them.
 */
import type { ElementDefinition } from 'cytoscape';
import type { SFAccountKyc, SFTxn } from '../services/statementFlow';
import { canonicalEntityId, looksLikeVpa, looksLikePhone } from '../case-canvas/entityJoin';
import { USER_STAGE } from './fusionSeed';

// Ingestion stages for the bank surface (see USER_INGEST_STEPS): the account
// arrives at stage 2, its counterparties + Dr/Cr edges at stage 3, so the play
// button reveals them after the typed identifiers (stage 1).
const ACCOUNT_STEP = USER_STAGE.ACCOUNT;
const CP_STEP = USER_STAGE.COUNTERPARTIES;
const SHORT = (s: string, n = 24) => (s.length > n ? s.slice(0, n - 1) + '…' : s);

function counterpartyName(t: SFTxn): string {
  const e = t.entities?.counterparty?.value || t.entities?.merchant?.value;
  if (e) return e;
  const raw = (t.raw_description || '').replace(/\s+/g, ' ').trim();
  return raw ? SHORT(raw) : 'counterparty';
}

function kycExtras(kyc?: SFAccountKyc | null): Record<string, string> | undefined {
  if (!kyc) return undefined;
  const extras: Record<string, string> = {};
  const put = (label: string, v?: string | null) => {
    const s = (v || '').trim();
    if (s) extras[label] = s;
  };
  put('holder', kyc.holder_name);
  put('account_number', kyc.account_number);
  put('ifsc', kyc.ifsc);
  put('branch', kyc.branch);
  put('location', kyc.bank_location);
  put('bank_phone', kyc.bank_phone);
  put('bank_email', kyc.bank_email);
  put('bank', kyc.bank);
  return Object.keys(extras).length ? extras : undefined;
}

export interface BankCounterpartyAgg {
  name: string;
  debit: number;
  credit: number;
  count: number;
}

export interface BankImportResult {
  elements: ElementDefinition[];
  accountNodeId: string;
  counterpartyCount: number;
  drTotal: number;
  crTotal: number;
  /** Aggregated counterparties (same top-N as canvas nodes). */
  counterparties: BankCounterpartyAgg[];
  /** Row-level ledger lines attached to the account node. */
  transactions: Array<{
    id: string;
    ref: string;
    direction: 'in' | 'out';
    counterparty: string;
    value: number;
    unit: string;
    ts: string;
    kind?: string;
  }>;
}

export function statementToElements(opts: {
  accountId: string;
  accountLabel: string;
  txns: SFTxn[];
  linkHubId?: string;               // e.g. 'id:rkumar' to tie into the case
  topN?: number;
  kyc?: SFAccountKyc | null;
}): BankImportResult {
  const { accountId, accountLabel, txns, linkHubId, topN = 40, kyc } = opts;
  const accNodeId = `sf:acct:${accountId}`;
  const elements: ElementDefinition[] = [];
  const extras = kycExtras(kyc);

  // Keep every parsed ledger line ON the account node so the inspector's Activity
  // panel lists all of them (the graph aggregates by counterparty; the row-level
  // detail lives here — that's how the officer "sees the 23 transactions").
  const txDetails = txns.map((t, i) => ({
    id: t.id || `sf:tx:${i}`,
    ref: t.raw_description || t.id || `row-${i}`,
    direction: (t.direction === 'Dr' ? 'out' : 'in') as 'in' | 'out',
    counterparty: counterpartyName(t),
    value: t.amount,
    unit: '₹',
    ts: t.txn_date,
    kind: (t.tags && t.tags[0]) || undefined,
  }));

  // aggregate by counterparty + direction
  const agg = new Map<string, { name: string; dr: number; cr: number; count: number }>();
  let drTotal = 0, crTotal = 0;
  for (const t of txns) {
    const name = counterpartyName(t);
    const key = name.toLowerCase();
    const a = agg.get(key) || { name, dr: 0, cr: 0, count: 0 };
    if (t.direction === 'Dr') { a.dr += t.amount; drTotal += t.amount; }
    else { a.cr += t.amount; crTotal += t.amount; }
    a.count += 1;
    agg.set(key, a);
  }

  const top = [...agg.values()].sort((x, y) => (y.dr + y.cr) - (x.dr + x.cr)).slice(0, topN);
  const riskFromVolume = (v: number) => Math.min(0.85, 0.3 + v / Math.max(1, drTotal + crTotal) * 0.7);

  const labelFromKyc = (kyc?.holder_name || '').trim()
    || (kyc?.account_number || '').trim()
    || accountLabel;

  elements.push({ data: {
    id: accNodeId, label: labelFromKyc, module: 'bank', type: 'account',
    risk_score: 0.7, size: 8, step: ACCOUNT_STEP, placement_role: 'subject',
    txn_count: txns.length, _txns: txDetails,
    ...(extras ? { module_extras: extras } : {}),
  } });

  top.forEach((c, i) => {
    // Identifier-shaped counterparties get their canonical entity id + type, so a
    // ledger line paying "9876543210@ybl" lands ON the VPA node the officer pasted
    // (or vice versa) — that meeting point IS the cross-surface link.
    let cpId: string; let cpType = 'person'; let cpModule = 'bank';
    if (looksLikeVpa(c.name)) {
      cpId = canonicalEntityId('vpa', c.name); cpType = 'vpa';
    } else if (looksLikePhone(c.name)) {
      cpId = canonicalEntityId('phone', c.name); cpType = 'phone'; cpModule = 'network';
    } else {
      cpId = `sf:cp:${i}:${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 20)}`;
    }
    elements.push({ data: {
      id: cpId, label: SHORT(c.name, 22), module: cpModule, type: cpType,
      risk_score: riskFromVolume(c.dr + c.cr), size: 4, step: CP_STEP, placement_role: 'context',
    } });
    if (c.dr > 0) {
      elements.push({ data: {
        id: `${accNodeId}->${cpId}`, source: accNodeId, target: cpId,
        edge_type: 'trace', determinism: 'deterministic', value: Math.round(c.dr), step: CP_STEP,
        _edgeLabel: `₹${Math.round(c.dr).toLocaleString('en-IN')}`,
      } });
    }
    if (c.cr > 0) {
      elements.push({ data: {
        id: `${cpId}->${accNodeId}`, source: cpId, target: accNodeId,
        edge_type: 'trace', determinism: 'deterministic', value: Math.round(c.cr), step: CP_STEP,
        _edgeLabel: `₹${Math.round(c.cr).toLocaleString('en-IN')}`,
      } });
    }
  });

  // Tie the imported account into the case identity hub, if present on the canvas.
  if (linkHubId) {
    elements.push({ data: {
      id: `${linkHubId}~${accNodeId}`, source: linkHubId, target: accNodeId,
      edge_type: 'cross_link', determinism: 'deterministic', step: ACCOUNT_STEP,
      _edgeLabel: 'imported statement',
    } });
  }

  const counterparties: BankCounterpartyAgg[] = top.map((c) => ({
    name: c.name,
    debit: Math.round(c.dr),
    credit: Math.round(c.cr),
    count: c.count,
  }));

  return {
    elements,
    accountNodeId: accNodeId,
    counterpartyCount: top.length,
    drTotal,
    crTotal,
    counterparties,
    transactions: txDetails,
  };
}
